import PropTypes from 'prop-types'
import { Button as AntdButton, Col, Flex, Form, Input, Row, Typography, Upload } from 'antd'
import { DeleteOutlined, PlusOutlined, UploadOutlined } from '@ant-design/icons'
import { useIntl } from 'react-intl'
import Button from '../../../common/Button'

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/svg+xml']
const LOGO_MAX_BYTES = 1024 * 1024

/**
 * The identity providers (OIDC/SAML) attached to a Cognito client, one row
 * each: the provider's name in the user pool, an optional display label, and
 * an optional logo for its sign-in button.
 *
 * A row's new logo file and its preview live in the form store alongside the
 * text fields (`logo_file`, `logo_preview`), so they can never drift out of
 * step with the row they belong to when rows are added or removed.
 */
const ProviderRow = ({ form, field, onInvalidLogo, onRemove }) => {
  const intl = useIntl()
  const path = ['providers', field.name]

  const selectLogo = (file) => {
    if (!LOGO_TYPES.includes(file.type) || file.size > LOGO_MAX_BYTES) {
      onInvalidLogo()
      return Upload.LIST_IGNORE
    }
    form.setFieldValue([...path, 'logo_file'], file)
    form.setFieldValue([...path, 'logo_preview'], URL.createObjectURL(file))
    form.setFieldValue([...path, 'clear_logo'], false)
    // Never upload on select; the file goes with the form on save
    return false
  }

  const removeLogo = () => {
    form.setFieldValue([...path, 'logo_file'], undefined)
    form.setFieldValue([...path, 'logo_preview'], undefined)
    form.setFieldValue([...path, 'clear_logo'], !!form.getFieldValue([...path, 'logo_url']))
  }

  return (
    <Row gutter={[12, 0]} align="top" data-testid="provider-row">
      <Col span={8}>
        <Form.Item
          name={[field.name, 'name']}
          label={intl.formatMessage({ id: 'cognitoClients.providerName' })}
          rules={[
            {
              required: true,
              whitespace: true,
              message: intl.formatMessage({ id: 'cognitoClients.providerNameRequired' }),
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                const name = (value || '').trim().toLowerCase()
                const rows = getFieldValue('providers') || []
                const sameName = rows.filter((row) => (row?.name || '').trim().toLowerCase() === name)
                if (!name || sameName.length < 2) return Promise.resolve()
                return Promise.reject(
                  new Error(intl.formatMessage({ id: 'cognitoClients.providerNameDuplicate' }))
                )
              },
            }),
          ]}
        >
          <Input
            data-testid="provider-name"
            placeholder={intl.formatMessage({ id: 'cognitoClients.providerNamePlaceholder' })}
          />
        </Form.Item>
      </Col>
      <Col span={7}>
        <Form.Item name={[field.name, 'label']} label={intl.formatMessage({ id: 'cognitoClients.providerLabel' })}>
          <Input
            data-testid="provider-label"
            placeholder={intl.formatMessage({ id: 'cognitoClients.providerLabelPlaceholder' })}
          />
        </Form.Item>
      </Col>
      <Col span={8}>
        <Form.Item label={intl.formatMessage({ id: 'cognitoClients.providerLogo' })}>
          <Form.Item noStyle shouldUpdate>
            {() => {
              const shown =
                form.getFieldValue([...path, 'logo_preview']) ||
                (!form.getFieldValue([...path, 'clear_logo']) && form.getFieldValue([...path, 'logo_url']))
              return (
                <Flex align="center" gap={8} wrap="wrap">
                  {shown && (
                    <img
                      src={shown}
                      alt=""
                      data-testid="provider-logo-preview"
                      className="provider-logo-preview"
                      style={{ maxHeight: 32, maxWidth: 96, objectFit: 'contain', background: '#fff', padding: 2 }}
                    />
                  )}
                  <Upload accept=".png,.jpg,.jpeg,.svg" beforeUpload={selectLogo} showUploadList={false} maxCount={1}>
                    <Button icon={<UploadOutlined />} testId="provider-logo-upload">
                      {intl.formatMessage({ id: 'cognitoClients.logoUpload' })}
                    </Button>
                  </Upload>
                  {shown && (
                    <Button onClick={removeLogo} testId="provider-logo-remove">
                      {intl.formatMessage({ id: 'cognitoClients.logoRemove' })}
                    </Button>
                  )}
                </Flex>
              )
            }}
          </Form.Item>
        </Form.Item>
      </Col>
      <Col span={1}>
        <Form.Item label=" ">
          <AntdButton
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={onRemove}
            aria-label={intl.formatMessage({ id: 'cognitoClients.removeProvider' })}
            data-testid="provider-remove"
          />
        </Form.Item>
      </Col>
    </Row>
  )
}

ProviderRow.propTypes = {
  form: PropTypes.object.isRequired,
  field: PropTypes.object.isRequired,
  onInvalidLogo: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
}

const ProviderRows = ({ form, onInvalidLogo }) => {
  const intl = useIntl()

  return (
    <Form.List name="providers">
      {(fields, { add, remove }) => (
        <Flex vertical gap={4}>
          {fields.length === 0 && (
            <Typography.Text type="secondary">
              {intl.formatMessage({ id: 'cognitoClients.noProviders' })}
            </Typography.Text>
          )}
          {fields.map((field) => (
            <ProviderRow
              key={field.key}
              form={form}
              field={field}
              onInvalidLogo={onInvalidLogo}
              onRemove={() => remove(field.name)}
            />
          ))}
          <Flex>
            <Button icon={<PlusOutlined />} onClick={() => add({ name: '', label: '' })} testId="add-provider">
              {intl.formatMessage({ id: 'cognitoClients.addProvider' })}
            </Button>
          </Flex>
          <Typography.Text type="secondary">{intl.formatMessage({ id: 'cognitoClients.logoHint' })}</Typography.Text>
        </Flex>
      )}
    </Form.List>
  )
}

ProviderRows.propTypes = {
  form: PropTypes.object.isRequired,
  onInvalidLogo: PropTypes.func.isRequired,
}

// The rows as the API takes them: JSON for the text, and each new logo as its
// own file field, `provider_logo_<index>`, matched by position.
export const appendProviders = (formData, rows) => {
  const providers = (rows || []).map((row, index) => {
    if (row?.logo_file) {
      formData.append(`provider_logo_${index}`, row.logo_file)
    }
    return {
      ...(row?.id ? { id: row.id } : {}),
      name: (row?.name || '').trim(),
      label: (row?.label || '').trim(),
      clear_logo: !!row?.clear_logo && !row?.logo_file,
    }
  })
  formData.append('providers', JSON.stringify(providers))
}

export const providerFormValues = (providers) =>
  (providers || []).map((provider) => ({
    id: provider.id,
    name: provider.name,
    label: provider.label || '',
    logo_url: provider.logo_url || null,
  }))

export default ProviderRows
